import { api, type Id } from "@repo/db"
import {
  TemplateFieldEditor,
  TemplateFieldEditorProvider,
  useTemplateFieldManagerDebounced,
} from "@repo/docs"
import { Button } from "@repo/shad/ui/button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@repo/shad/ui/dialog"
import { createFileRoute, useNavigate } from "@tanstack/react-router"
import { useMutation } from "convex/react"
import { Plus } from "lucide-react"

import { DocsFeatureDisabledState } from "@/components/docs/docs-feature-disabled-state"
import { useDocsFeatureEnabled } from "@/features/docs/feature-gate"
import { useConvex } from "@/hooks/use-convex"
import { useTemplateVersioning } from "@/hooks/use-template-versioning"

import { ErrorState, LoadingState } from "../../../../components/fields/LoadingStates"
import { usePdfUrl } from "../../../../hooks/usePdfUrl"

export const Route = createFileRoute("/$dealership/deal-packets/templates/$templateId/fields")({
  staticData: {
    breadcrumb: "Fields",
  },
  component: TemplateFieldsPage,
})

function TemplateFieldsPage() {
  const navigate = useNavigate()
  const context = Route.useRouteContext()
  const { templateId } = Route.useParams()

  const { data: template } = useConvex(api.documents.queries.getTemplate, {
    templateId: templateId as Id<"documentTemplates">,
  })
  const isPdfTemplate = !!template && (template.templateFormat ?? "pdf") === "pdf"
  const { pdfUrl, isLoading: isLoadingPdf } = usePdfUrl(templateId, isPdfTemplate)

  const { data: fieldsData } = useConvex(
    api.documents.templateFields.queries.getTemplateFields,
    {
      templateId: templateId as Id<"documentTemplates">,
    },
    { enabled: !!template },
  )
  const { data: customMergeFieldsData } = useConvex(
    api.documents.customMergeFields.queries.listForTemplate,
    { templateId: templateId as Id<"documentTemplates"> },
    { enabled: !!template },
  )
  const createCustomMergeField = useMutation(
    api.documents.customMergeFields.mutations.createForTemplate,
  )
  const { isCreatingEditableVersion, createEditableVersion } = useTemplateVersioning()
  const fields = fieldsData || []

  const {
    selectedFieldId,
    selectedFieldType,
    setSelectedFieldType,
    clearSelection,
    handleFieldCreate,
    handleFieldUpdate,
    handleFieldDelete,
    setSelectedFieldId,
  } = useTemplateFieldManagerDebounced(templateId, fields)

  const dealershipSlug =
    context && "dealership" in context && context.dealership ? context.dealership.slug : ""
  const dealershipId =
    context && "dealership" in context && context.dealership ? context.dealership._id : undefined
  const { enabled: docsEnabled, isPending: docsFeaturePending } =
    useDocsFeatureEnabled(dealershipId)

  const handleBack = () => {
    void navigate({
      to: "/$dealership/deal-packets/templates/$templateId",
      params: { dealership: dealershipSlug, templateId },
    })
  }

  if (docsFeaturePending) {
    return <LoadingState />
  }

  if (!docsEnabled) {
    return (
      <DocsFeatureDisabledState
        title="Field mapping is turned off"
        description="This docs workflow is disabled for this dealership. Enable dealSigningDocs before mapping PDF fields."
        action={
          <Button variant="outline" size="sm" onClick={handleBack}>
            Back to Template
          </Button>
        }
      />
    )
  }

  if (!template || isLoadingPdf) {
    return <LoadingState />
  }

  if (template.templateScope === "global") {
    return (
      <>
        <LoadingState />
        <Dialog
          open
          onOpenChange={(open) => {
            if (!open) {
              handleBack()
            }
          }}
        >
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>Create a dealership version to map fields</DialogTitle>
              <DialogDescription>
                Shared templates stay unchanged. Create a dealership version before editing field
                mappings for signing.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button variant="outline" onClick={handleBack} disabled={isCreatingEditableVersion}>
                Cancel
              </Button>
              <Button
                onClick={() => {
                  void (async () => {
                    const result = await createEditableVersion(template)
                    void navigate({
                      to: "/$dealership/deal-packets/templates/$templateId/fields",
                      params: {
                        dealership: dealershipSlug,
                        templateId: result.templateId,
                      },
                      replace: true,
                    })
                  })()
                }}
                disabled={isCreatingEditableVersion}
              >
                <Plus className="mr-2 h-4 w-4" />
                Create Dealership Version
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </>
    )
  }

  if ((template.templateFormat ?? "pdf") !== "pdf") {
    return (
      <ErrorState message="Field mapping is only available for PDF templates" onBack={handleBack} />
    )
  }

  if (!pdfUrl) {
    return <ErrorState onBack={handleBack} />
  }

  return (
    <TemplateFieldEditorProvider
      templateId={templateId}
      templateName={template.name}
      pdfUrl={pdfUrl}
      fields={fields}
      customMergeFields={customMergeFieldsData ?? []}
      selectedFieldId={selectedFieldId}
      selectedFieldType={selectedFieldType}
      setSelectedFieldId={setSelectedFieldId}
      setSelectedFieldType={setSelectedFieldType}
      clearSelection={clearSelection}
      onFieldCreate={handleFieldCreate}
      onFieldUpdate={handleFieldUpdate}
      onFieldDelete={handleFieldDelete}
      onCreateCustomMergeField={(input) =>
        createCustomMergeField({
          templateId: templateId as Id<"documentTemplates">,
          ...input,
        })
      }
      onBack={handleBack}
    >
      <TemplateFieldEditor />
    </TemplateFieldEditorProvider>
  )
}
